// tipos.ts — o vocabulário das abas "Descontos" e "Taxas".
//
// Tudo o que as duas telas precisam concordar mora aqui: os tipos das regras, a
// leitura do que já está gravado no token, a validação e a montagem do payload.
//
// A REGRA DE OURO desta pasta: "%" e "R$" NUNCA no mesmo campo. Toda regra tem
// `forma` ("percentual" | "reais") e `valor` (número puro), em campos separados.
// O defeito que existe em produção hoje é exatamente uma coluna com "3" e "10%"
// misturados — sem saber a forma, ninguém consegue nem exibir, nem somar, nem
// auditar o que foi dado de desconto.

/** Tipos de `acd."ActPeriodTypeInfo"`, os mesmos que o PeriodTypeModal já usa. */
export const PERIOD_TYPE_INFO_DESCONTOS = 1;
export const PERIOD_TYPE_INFO_TAXAS = 2;

export const NOME_INFO_DESCONTOS = "discounts";
export const NOME_INFO_TAXAS = "taxs";

/**
 * A rota que não existe. Conferido nos routers do backend: são 10, nenhum de
 * período — `acd."ActPeriodTypeInfo"` não é exposta por nenhum deles, mesmo com
 * a tabela e os tipos 1/2 já existindo no banco.
 *
 * O nome aparece na tela ANTES de o usuário digitar, e de novo na recusa do
 * salvamento. Formulário que aceita digitação e some com o dado sem dizer o
 * porquê é a mentira que este texto existe para impedir.
 */
export const ROTA_QUE_FALTA = "periodTypeInfo (POST/PATCH)";

/**
 * O texto vem inteiro daqui para as duas telas ficarem obrigadas a dizer a
 * mesma coisa: cadastrar a regra não conserta a conta.
 *
 * O que este aviso afirma é só o que dá para provar aqui: o formato gravado
 * hoje (conferido em `src/store/exemplo-de-decryptedInfoToken.json`) é um mapa
 * `nome -> texto` em que "3,00" e "10%" convivem no mesmo campo, e nenhuma
 * linha de cálculo mora nesta Central. Como cada consumidor desse texto decide
 * sozinho o que a unidade quer dizer, o mesmo cadastro pode sair com contas
 * diferentes em telas diferentes — e nada nesta tela muda isso.
 */
export const AVISO_ARITMETICA =
  "Esta tela cadastra a REGRA. Ela não faz, nem corrige, nenhum cálculo. O que " +
  "está gravado hoje guarda o valor como TEXTO, com \"3,00\" e \"10%\" no mesmo " +
  "campo: quem consome esse texto decide sozinho se é reais ou percentual, e é " +
  "por isso que o mesmo cadastro pode aparecer com contas diferentes em telas " +
  "diferentes. Separar forma e valor, o que este formulário faz, é o começo; " +
  "acertar a aritmética de quem lê é trabalho no OPS, em card próprio.";

export type Tom = "ok" | "erro" | "atencao";
export interface Aviso {
  tom: Tom;
  texto: string;
}

export type Forma = "percentual" | "reais";

export type TipoDeDesconto =
  | "livre"
  | "convenio"
  | "beneficioFuncionario"
  | "campanha"
  | "cortesia";

export type NaturezaDeTaxa =
  | "servico"
  | "couvert"
  | "entrega"
  | "embalagem"
  | "outra";

export type BaseDeTaxa =
  | "subtotal"
  | "subtotalLiquidoDeDesconto"
  | "porPessoa"
  | "fixo";

/**
 * Terceiro estado de propósito. "" NÃO é "não": é "ninguém decidiu ainda".
 * `entraNoFaturamento` e `entraNaBaseDeImposto` nascem assim porque a decisão é
 * do contador do cliente, não do sistema — gravar `false` de brinde seria
 * inventar uma classificação fiscal em nome dele.
 */
export type ADefinir = "" | "sim" | "nao";

export const OPCOES_FORMA: { valor: Forma; rotulo: string }[] = [
  { valor: "percentual", rotulo: "Percentual (%)" },
  { valor: "reais", rotulo: "Valor em reais (R$)" },
];

export const OPCOES_TIPO_DESCONTO: { valor: TipoDeDesconto; rotulo: string }[] =
  [
    { valor: "livre", rotulo: "Livre (o operador escolhe)" },
    { valor: "convenio", rotulo: "Convênio (empresa parceira)" },
    { valor: "beneficioFuncionario", rotulo: "Benefício de funcionário" },
    { valor: "campanha", rotulo: "Campanha / promoção" },
    { valor: "cortesia", rotulo: "Cortesia" },
  ];

export const OPCOES_NATUREZA_TAXA: { valor: NaturezaDeTaxa; rotulo: string }[] =
  [
    { valor: "servico", rotulo: "Serviço (os 10%)" },
    { valor: "couvert", rotulo: "Couvert" },
    { valor: "entrega", rotulo: "Entrega" },
    { valor: "embalagem", rotulo: "Embalagem" },
    { valor: "outra", rotulo: "Outra" },
  ];

export const OPCOES_BASE_TAXA: { valor: BaseDeTaxa; rotulo: string }[] = [
  { valor: "subtotal", rotulo: "Subtotal da conta" },
  {
    valor: "subtotalLiquidoDeDesconto",
    rotulo: "Subtotal já com os descontos aplicados",
  },
  { valor: "porPessoa", rotulo: "Por pessoa na mesa" },
  { valor: "fixo", rotulo: "Valor fixo por conta" },
];

/**
 * Padrão decidido pelo DONO DO PRODUTO: desconto acima de 10% pede autorizador.
 * Fica nomeado como constante para a tela poder dizer de onde o 10 veio — número
 * que aparece sozinho num campo vira "o sistema mandou" e ninguém sabe se pode
 * mudar.
 */
export const AUTORIZADOR_PADRAO_PERCENTUAL = "10";

/** Também decisão do dono do produto: couvert é sobre o subtotal, antes do desconto. */
export const PADRAO_COUVERT: { base: BaseDeTaxa; incideAntesDoDesconto: boolean } =
  { base: "subtotal", incideAntesDoDesconto: true };

/**
 * O formulário guarda número como STRING porque `<input type="number">` devolve
 * string, e "" precisa continuar sendo "campo vazio" — virar 0 cedo demais
 * transformaria "não preenchi o teto" em "teto de R$ 0,00", que é o oposto.
 */
export interface FormDesconto {
  id: string;
  nome: string;
  tipo: TipoDeDesconto;
  forma: Forma;
  valor: string;
  vigenciaInicio: string;
  vigenciaFim: string;
  tetoPorConta: string;
  exigeAutorizadorAcimaDe: string;
  cnpjConvenio: string;
  entraNoCMV: "" | "cmv" | "despesa";
}

export interface FormTaxa {
  id: string;
  nome: string;
  natureza: NaturezaDeTaxa;
  forma: Forma;
  valor: string;
  base: BaseDeTaxa;
  incideAntesDoDesconto: boolean;
  entraNoFaturamento: ADefinir;
  entraNaBaseDeImposto: ADefinir;
}

export function descontoVazio(): FormDesconto {
  return {
    id: "",
    nome: "",
    tipo: "livre",
    forma: "percentual",
    valor: "",
    vigenciaInicio: "",
    vigenciaFim: "",
    tetoPorConta: "",
    exigeAutorizadorAcimaDe: AUTORIZADOR_PADRAO_PERCENTUAL,
    cnpjConvenio: "",
    entraNoCMV: "",
  };
}

export function taxaVazia(): FormTaxa {
  return {
    id: "",
    nome: "",
    natureza: "servico",
    forma: "percentual",
    valor: "",
    base: "subtotal",
    incideAntesDoDesconto: false,
    // Nascem vazios de propósito — ver o comentário de ADefinir.
    entraNoFaturamento: "",
    entraNaBaseDeImposto: "",
  };
}

/** Um turno de caixa (`ActPeriodType`) com as regras já gravadas nele. */
export interface TurnoDeCaixa {
  uuid: string;
  nome: string;
  /** Nome da loja (subsidiária) dona do turno. Turno chamado "Almoço" existe em
   *  toda loja: sem a loja no cartão, dois cartões idênticos e nenhuma pista. */
  loja: string;
  /** Regras lidas do token, do jeito que estão gravadas. */
  regras: any[];
  /** JSON quebrado no banco: a tela avisa em vez de branquear. */
  corrompida: boolean;
  /** Ainda não existe linha de info deste tipo para este turno. */
  existe: boolean;
}

/**
 * Regra que veio no FORMATO ANTIGO, o que está gravado hoje em produção:
 * `periodTypeInfoValue` é um mapa `nome -> texto`, tipo
 * `{"Desconto 10%": "10", "Desconto 30%": "30"}` nos descontos e
 * `{"Taxa de Entrega": "3,00", "Couver": "10%"}` nas taxas.
 *
 * Repare na segunda: "3,00" é reais e "10%" é percentual, no MESMO campo. É
 * exatamente o defeito que esta tela existe para não repetir — e por isso o
 * texto guardado é preservado como está, em `valorBruto`, sem ninguém adivinhar
 * a unidade. Chutar "percentual" aqui transformaria R$ 3,00 de entrega em 3% da
 * conta, calado.
 */
export interface RegraLegada {
  id: string;
  nome: string;
  valorBruto: string;
  legado: true;
}

/** Reconhece o mapa `nome -> texto` do formato antigo. */
function ehMapaLegado(bruto: any): boolean {
  return (
    !!bruto &&
    typeof bruto === "object" &&
    !Array.isArray(bruto) &&
    !Array.isArray(bruto.regras)
  );
}

function lerRegrasLegadas(bruto: any): RegraLegada[] {
  return Object.entries(bruto).map(([nome, valor]) => ({
    id: `legado:${nome}`,
    nome,
    valorBruto: typeof valor === "string" ? valor : JSON.stringify(valor),
    legado: true as const,
  }));
}

/**
 * Lê os turnos de caixa do infoToken, não de HTTP — mesmo motivo de
 * MeiosPagamento.tsx: o token já traz a travessia inteira e as rotas
 * equivalentes não fazem `include`.
 *
 * A travessia é `organization -> companies -> subsidiaries -> periodTypes`, a
 * MESMA de storesSection/Periodos.tsx e a única que existe no token real
 * (conferido em `src/store/exemplo-de-decryptedInfoToken.json`).
 * `organization.periodTypes` não existe no payload — ler de lá devolvia lista
 * vazia sempre, e a tela inteira caía no "nenhum turno cadastrado" mesmo com
 * turnos gravados.
 *
 * O identificador do turno tem dois nomes na vida real (`periodTypeUuid` no
 * backend novo, `periodTypeId` no legado do infoToken). Ler os dois evita uma
 * lista que aparece com nome e sem chave — que salvaria contra `undefined`.
 */
export function lerTurnosDeCaixa(
  decryptedInfoToken: any,
  periodTypeInfoType: number
): TurnoDeCaixa[] {
  const empresas = decryptedInfoToken?.organization?.companies ?? [];
  const turnos: { turno: any; loja: string }[] = [];

  for (const empresa of empresas) {
    for (const filial of empresa?.subsidiaries ?? []) {
      const loja = filial?.subsidiaryName ?? "Loja sem nome";
      for (const turno of filial?.periodTypes ?? []) {
        turnos.push({ turno, loja });
      }
    }
  }

  return turnos.map(({ turno, loja }) => {
    const uuid = String(turno?.periodTypeUuid ?? turno?.periodTypeId ?? "");
    const base = {
      uuid,
      nome: turno?.periodTypeName ?? "Turno sem nome",
      loja,
    };
    const linha = (turno?.periodTypeInfo ?? []).find(
      (info: any) => Number(info?.periodTypeInfoType) === periodTypeInfoType
    );

    if (!linha) {
      return { ...base, regras: [], corrompida: false, existe: false };
    }

    try {
      const bruto = JSON.parse(linha.periodTypeInfoValue ?? "{}");

      // Aceita `{ regras: [...] }` (o formato que esta tela monta), um array
      // cru, e o mapa `nome -> texto` do formato antigo. O antigo NÃO vira
      // lista vazia: sumir com regra que existe no banco é a mentira que esta
      // tela toda existe para não contar.
      if (Array.isArray(bruto)) {
        return { ...base, regras: bruto, corrompida: false, existe: true };
      }
      if (Array.isArray(bruto?.regras)) {
        return { ...base, regras: bruto.regras, corrompida: false, existe: true };
      }
      if (ehMapaLegado(bruto)) {
        return {
          ...base,
          regras: lerRegrasLegadas(bruto),
          corrompida: false,
          existe: true,
        };
      }
      // Nem lista, nem mapa: número, texto solto ou null. Não dá para ler como
      // regra, então a tela avisa em vez de mostrar tabela vazia.
      return { ...base, regras: [], corrompida: true, existe: true };
    } catch {
      return { ...base, regras: [], corrompida: true, existe: true };
    }
  });
}

/** Quantas regras deste turno vieram no formato antigo. */
export function contarLegadas(regras: any[]): number {
  return regras.filter((regra: any) => regra?.legado === true).length;
}

/** Identificador da regra dentro do JSON. Só serve para substituir na edição. */
function novoId(): string {
  const aleatorio: any = (globalThis as any)?.crypto;
  if (typeof aleatorio?.randomUUID === "function") return aleatorio.randomUUID();
  return `regra-${Date.now()}`;
}

function numeroOuNulo(texto: string): number | null {
  if (texto.trim() === "") return null;
  const numero = Number(texto.replace(",", "."));
  return Number.isFinite(numero) ? numero : null;
}

function soDigitos(texto: string): string {
  return texto.replace(/\D/g, "");
}

/**
 * Devolve a lista de problemas em português, para o aviso da tela. Lista, e não
 * "primeiro erro": quem preencheu quatro campos errados merece corrigir os
 * quatro de uma vez, não descobrir um por clique.
 */
export function validarDesconto(form: FormDesconto): string[] {
  const problemas: string[] = [];

  if (!form.nome.trim()) problemas.push("Dê um nome à regra de desconto.");

  const valor = numeroOuNulo(form.valor);
  if (valor === null || valor <= 0) {
    problemas.push("O valor do desconto precisa ser maior que zero.");
  } else if (form.forma === "percentual" && valor > 100) {
    problemas.push("Desconto percentual não pode passar de 100%.");
  }

  if (form.vigenciaInicio && form.vigenciaFim) {
    if (form.vigenciaFim < form.vigenciaInicio) {
      problemas.push("O fim da vigência é anterior ao início.");
    }
  }

  const teto = numeroOuNulo(form.tetoPorConta);
  if (form.tetoPorConta.trim() !== "" && (teto === null || teto <= 0)) {
    problemas.push(
      "O teto por conta, quando preenchido, precisa ser maior que zero em reais."
    );
  }

  const autorizador = numeroOuNulo(form.exigeAutorizadorAcimaDe);
  if (autorizador === null || autorizador < 0 || autorizador > 100) {
    problemas.push(
      "O limite que exige autorizador é um percentual entre 0 e 100."
    );
  }

  if (form.tipo === "convenio") {
    const cnpj = soDigitos(form.cnpjConvenio);
    if (cnpj.length !== 14) {
      problemas.push(
        "Convênio precisa do CNPJ da empresa parceira, com 14 dígitos."
      );
    }
  }

  // Sem escolha explícita, o desconto não tem classificação contábil — e adivinhar
  // por ele erra o CMV do mês inteiro, em silêncio.
  if (form.entraNoCMV === "") {
    problemas.push(
      "Diga se este desconto entra no CMV ou vira despesa — o sistema não escolhe por você."
    );
  }

  return problemas;
}

export function validarTaxa(form: FormTaxa): string[] {
  const problemas: string[] = [];

  if (!form.nome.trim()) problemas.push("Dê um nome à taxa.");

  const valor = numeroOuNulo(form.valor);
  if (valor === null || valor <= 0) {
    problemas.push("O valor da taxa precisa ser maior que zero.");
  } else if (form.forma === "percentual" && valor > 100) {
    problemas.push("Taxa percentual não pode passar de 100%.");
  }

  // "20% por pessoa" não quer dizer nada: percentual de quê? Bases que já são
  // um multiplicador (por pessoa, fixo) só fazem sentido em reais.
  if (
    form.forma === "percentual" &&
    (form.base === "porPessoa" || form.base === "fixo")
  ) {
    const rotulo =
      form.base === "porPessoa" ? "por pessoa na mesa" : "valor fixo por conta";
    problemas.push(
      `Taxa em percentual não combina com a base "${rotulo}": percentual de quê? ` +
        "Use reais, ou troque a base para o subtotal."
    );
  }

  return problemas;
}

/**
 * Monta o valor da regra do jeito que ela seria gravada. Campo vazio vira
 * `null` — nunca 0, nunca `false`: os dois seriam uma decisão que ninguém tomou.
 */
export function regraDoDesconto(form: FormDesconto) {
  return {
    id: form.id || novoId(),
    nome: form.nome.trim(),
    tipo: form.tipo,
    forma: form.forma,
    valor: numeroOuNulo(form.valor),
    vigenciaInicio: form.vigenciaInicio || null,
    vigenciaFim: form.vigenciaFim || null,
    tetoPorConta: numeroOuNulo(form.tetoPorConta),
    exigeAutorizadorAcimaDe: numeroOuNulo(form.exigeAutorizadorAcimaDe),
    cnpjConvenio:
      form.tipo === "convenio" ? soDigitos(form.cnpjConvenio) : null,
    entraNoCMV: form.entraNoCMV === "cmv",
  };
}

export function regraDaTaxa(form: FormTaxa) {
  return {
    id: form.id || novoId(),
    nome: form.nome.trim(),
    natureza: form.natureza,
    forma: form.forma,
    valor: numeroOuNulo(form.valor),
    base: form.base,
    incideAntesDoDesconto: form.incideAntesDoDesconto,
    // `null` é o "a definir com o contador" atravessando até o payload. Mandar
    // `false` seria responder pelo contador do cliente.
    entraNoFaturamento:
      form.entraNoFaturamento === "" ? null : form.entraNoFaturamento === "sim",
    entraNaBaseDeImposto:
      form.entraNaBaseDeImposto === ""
        ? null
        : form.entraNaBaseDeImposto === "sim",
  };
}

/** Substitui a regra de mesmo id, ou acrescenta quando é nova. */
export function aplicarRegra(regras: any[], regra: any): any[] {
  const indice = regras.findIndex((atual: any) => atual?.id === regra.id);
  if (indice < 0) return [...regras, regra];
  const copia = [...regras];
  copia[indice] = regra;
  return copia;
}

/**
 * O corpo que a rota inexistente receberia. Ele é montado ANTES da recusa de
 * propósito: quando `periodTypeInfo` existir, é só o `case` do shell passar a
 * mandar isto — e enquanto não existe, o payload correto é o que prova que a
 * tela não está inventando formato.
 */
export function payloadDeRegras(
  turno: TurnoDeCaixa,
  regras: any[],
  periodTypeInfoType: number
) {
  const ehDesconto = periodTypeInfoType === PERIOD_TYPE_INFO_DESCONTOS;
  return {
    periodTypeUuid: turno.uuid,
    periodTypeInfoType,
    periodTypeInfoName: ehDesconto ? NOME_INFO_DESCONTOS : NOME_INFO_TAXAS,
    periodTypeInfoDescription: ehDesconto
      ? "Regras de desconto do turno de caixa"
      : "Taxas do turno de caixa",
    periodTypeInfoValue: JSON.stringify({ versao: 1, regras }),
    rotaNecessaria: ROTA_QUE_FALTA,
  };
}

/**
 * Exibição: a unidade vem da `forma`, nunca de um caractere digitado no valor.
 *
 * Quando a `forma` não é nenhuma das duas — regra antiga, gravada sem unidade —
 * o número sai SEM unidade nenhuma. Cair no `R$` por descarte era mostrar
 * "R$ 10,00" ao lado de uma coluna "Forma" escrita "%": duas unidades para o
 * mesmo número, que é o defeito original com outra roupa.
 */
export function mostrarValor(forma: any, valor: any): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return "—";
  if (forma === "percentual") {
    return `${numero.toLocaleString("pt-BR")}%`;
  }
  if (forma === "reais") {
    return numero.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }
  return numero.toLocaleString("pt-BR");
}

/** Símbolo da coluna "Forma". Sem unidade gravada, não se inventa uma. */
export function mostrarForma(forma: any): string {
  if (forma === "reais") return "R$";
  if (forma === "percentual") return "%";
  return "—";
}

export function mostrarReais(valor: any): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return "—";
  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/** Rótulo de um valor de select, para a tabela do que está gravado. */
export function rotuloDe(
  opcoes: { valor: string; rotulo: string }[],
  valor: any
): string {
  const achado = opcoes.find((opcao) => opcao.valor === valor);
  return achado ? achado.rotulo : String(valor ?? "—");
}

/** "a definir" continua aparecendo como "a definir" na tabela, e não como "não". */
export function mostrarADefinir(valor: any): string {
  if (valor === true) return "sim";
  if (valor === false) return "não";
  return "a definir com o contador";
}
